import { useEffect, useRef, useState } from "react";
import { useDataChannel, useLocalParticipant, useRoomContext } from "@livekit/components-react";
import { Tldraw, createTLStore, defaultShapeUtils, type Editor, type TLRecord } from "tldraw";
import "tldraw/tldraw.css";

const TOPIC = "whiteboard";
const SYNC_KIND = { reliable: true } as const;

interface DiffMsg {
  type: "diff";
  changes: { added?: Record<string, TLRecord>; updated?: Record<string, [TLRecord, TLRecord]>; removed?: Record<string, TLRecord> };
  from: string;
  seq: number;
}
interface SnapshotRequestMsg { type: "snapshot_request"; from: string }
interface SnapshotMsg { type: "snapshot"; records: TLRecord[]; from: string }
type Msg = DiffMsg | SnapshotRequestMsg | SnapshotMsg;

export function Whiteboard(): JSX.Element {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const [store] = useState(() => createTLStore({ shapeUtils: defaultShapeUtils }));
  const editorRef = useRef<Editor | null>(null);
  const seqRef = useRef(0);
  const applyingRemoteRef = useRef(false);

  // Outgoing: listen to local store changes, broadcast as diff.
  useEffect(() => {
    const unsub = store.listen(
      (entry) => {
        if (applyingRemoteRef.current) return;
        if (entry.source !== "user") return;
        seqRef.current += 1;
        const msg: DiffMsg = {
          type: "diff",
          changes: entry.changes as DiffMsg["changes"],
          from: localParticipant.identity,
          seq: seqRef.current,
        };
        const payload = new TextEncoder().encode(JSON.stringify(msg));
        void localParticipant.publishData(payload, { topic: TOPIC, reliable: true });
      },
      { source: "user", scope: "document" },
    );
    return () => unsub();
  }, [store, localParticipant]);

  // Incoming: apply diffs / snapshots.
  useDataChannel(TOPIC, (msgEvent) => {
    try {
      const text = new TextDecoder().decode(msgEvent.payload);
      const msg = JSON.parse(text) as Msg;
      if (msg.from === localParticipant.identity) return;
      if (msg.type === "diff") {
        applyingRemoteRef.current = true;
        store.mergeRemoteChanges(() => {
          const { added, updated, removed } = msg.changes;
          if (added) store.put(Object.values(added));
          if (updated) store.put(Object.values(updated).map(([, next]) => next));
          if (removed) store.remove(Object.keys(removed) as TLRecord["id"][]);
        });
        applyingRemoteRef.current = false;
      } else if (msg.type === "snapshot_request") {
        const records = store.allRecords();
        const reply: SnapshotMsg = { type: "snapshot", records, from: localParticipant.identity };
        const payload = new TextEncoder().encode(JSON.stringify(reply));
        void localParticipant.publishData(payload, {
          topic: TOPIC,
          reliable: true,
          destinationIdentities: [msg.from],
        });
      } else if (msg.type === "snapshot") {
        applyingRemoteRef.current = true;
        store.mergeRemoteChanges(() => {
          store.clear();
          store.put(msg.records);
        });
        applyingRemoteRef.current = false;
      }
    } catch (err) {
      console.warn("whiteboard msg parse failed", err);
    }
  });

  // On mount: ask current participants for a snapshot.
  useEffect(() => {
    const t = setTimeout(() => {
      if (room.numParticipants <= 1) return; // only us
      const req: SnapshotRequestMsg = { type: "snapshot_request", from: localParticipant.identity };
      const payload = new TextEncoder().encode(JSON.stringify(req));
      void localParticipant.publishData(payload, { topic: TOPIC, reliable: true });
    }, 500);
    return () => clearTimeout(t);
  }, [room, localParticipant]);

  return (
    <div className="h-full w-full bg-white">
      <Tldraw
        store={store}
        onMount={(editor) => {
          editorRef.current = editor;
        }}
      />
    </div>
  );
}
